import type {IntlShape} from 'react-intl';
import {BRAND_SWATCHES} from './brandSwatches';
import type {PlateValue, QuoteDocumentData, QuoteTemplate} from '../resolver/types';

export const DEFAULT_FILE_NAME_TEMPLATE = 'quote_{reference}_{date}';

const EMPTY_PLATE: PlateValue = [{type: 'p', children: [{text: ''}]}];

/** Blank-line separated text → one paragraph block per segment. */
export const textToParagraphs = (text: string): PlateValue => {
	const paragraphs = text
		.split(/\n\n+/)
		.map(p => p.trim())
		.filter(Boolean);
	if (paragraphs.length === 0) return EMPTY_PLATE;
	return paragraphs.map(p => ({type: 'p', children: [{text: p}]}));
};

/**
 * Blank-line separated text → alternating heading/body blocks: the first line of each segment
 * becomes a heading block, the remaining lines one paragraph block.
 */
export const textToTermBlocks = (text: string): PlateValue => {
	const blocks: PlateValue = [];
	for (const segment of text.split(/\n\n+/)) {
		const lines = segment
			.split('\n')
			.map(l => l.trim())
			.filter(Boolean);
		if (lines.length === 0) continue;
		blocks.push({type: 'h1', children: [{text: lines[0]}]});
		if (lines.length > 1) {
			blocks.push({type: 'p', children: [{text: lines.slice(1).join(' ')}]});
		}
	}
	return blocks.length > 0 ? blocks : EMPTY_PLATE;
};

/**
 * The template a new designer session starts from. Narrative defaults are localized copy with
 * the project / client names already interpolated, so the very first preview renders sensibly.
 */
export const buildDefaultQuoteTemplate = (intl: IntlShape, data: QuoteDocumentData): QuoteTemplate => {
	const values = {
		projectName: data.projectName,
		clientName: data.clientName || data.projectName,
	};
	return {
		brandColor: BRAND_SWATCHES[0],
		font: 'helvetica',
		showIntroduction: true,
		introduction: textToParagraphs(intl.formatMessage({id: 'quote_designer.default.introduction'}, values)),
		showScopeOfWork: true,
		scopeOfWork: textToParagraphs(intl.formatMessage({id: 'quote_designer.default.scope'}, values)),
		showTerms: true,
		terms: textToTermBlocks(intl.formatMessage({id: 'quote_designer.default.terms'})),
		rollup: 'roles',
		showTime: true,
		showRate: true,
		showRoleTotals: true,
		showPhaseSubtotals: true,
		showPhaseExpenses: true,
		showDeliverables: true,
		showSubtotal: true,
		showExpensesRow: true,
		showGrandTotal: true,
		fileNameTemplate: DEFAULT_FILE_NAME_TEMPLATE,
	};
};

/** Structural fallback used only while the page is deciding to redirect away. */
export const EMPTY_QUOTE_TEMPLATE: QuoteTemplate = {
	brandColor: BRAND_SWATCHES[0],
	font: 'helvetica',
	showIntroduction: true,
	introduction: EMPTY_PLATE,
	showScopeOfWork: true,
	scopeOfWork: EMPTY_PLATE,
	showTerms: true,
	terms: EMPTY_PLATE,
	rollup: 'roles',
	showTime: true,
	showRate: true,
	showRoleTotals: true,
	showPhaseSubtotals: true,
	showPhaseExpenses: true,
	showDeliverables: true,
	showSubtotal: true,
	showExpensesRow: true,
	showGrandTotal: true,
	fileNameTemplate: DEFAULT_FILE_NAME_TEMPLATE,
};
